import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withTranslate from 'containers/widgets/withTranslate';
import Shelf from 'components/widgets/Shelf';
import toaster from 'utils/toaster';
import aiContextService from '../service';
import { TOAST_CONFIG } from '../constants';
import VersionRow from './VersionRow';
import css from './VersionsShelf.css';

const VERSIONS_SHELF_PREFIX = 'STATIC.PAGES.AI_CONTEXT.VERSIONS_SHELF';
const TOASTS_PREFIX = 'STATIC.PAGES.AI_CONTEXT.TOASTS';

const VersionsShelf = ({
    open,
    onClose,
    onPreview,
    onRestore,
    currentVersionId = null,
    previewingVersionId = null,
    loadingPreviewVersionId = null,
    restoringVersionId = null,
    translate,
}) => {
    const [versions, setVersions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadedForVersionId, setLoadedForVersionId] = useState(undefined);

    useEffect(() => {
        if (!open) return undefined;
        if (loadedForVersionId === currentVersionId) return undefined;
        let cancelled = false;
        setIsLoading(true);
        aiContextService
            .listVersions()
            .then(loaded => {
                if (!cancelled) {
                    setVersions(loaded);
                    setLoadedForVersionId(currentVersionId);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    toaster.error(translate(`${TOASTS_PREFIX}.VERSIONS_LOAD_FAILED`), TOAST_CONFIG);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, currentVersionId, loadedForVersionId, translate]);

    const isAnyPreviewLoading = loadingPreviewVersionId != null;
    const isAnyRestoring = restoringVersionId != null;

    return (
        <Shelf
            open={open}
            onClose={onClose}
            headerText={`${VERSIONS_SHELF_PREFIX}.HEADER`}
            headerIconName="history"
            shelfSize="medium"
            contentContainerClass={css.shelfContent}
        >
            {isLoading && <div className={css.state}>{translate(`${VERSIONS_SHELF_PREFIX}.LOADING`)}</div>}
            {!isLoading && versions.length === 0 && (
                <div className={css.state}>{translate(`${VERSIONS_SHELF_PREFIX}.EMPTY`)}</div>
            )}
            {!isLoading && versions.length > 0 && (
                <ul className={css.list}>
                    {versions.map(version => (
                        <VersionRow
                            key={version.id}
                            version={version}
                            isCurrent={currentVersionId === version.id}
                            isPreviewing={previewingVersionId === version.id}
                            isLoadingThisPreview={loadingPreviewVersionId === version.id}
                            isRestoringThis={restoringVersionId === version.id}
                            isAnyPreviewLoading={isAnyPreviewLoading}
                            isAnyRestoring={isAnyRestoring}
                            onPreview={onPreview}
                            onRestore={onRestore}
                        />
                    ))}
                </ul>
            )}
        </Shelf>
    );
};

VersionsShelf.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onPreview: PropTypes.func.isRequired,
    onRestore: PropTypes.func.isRequired,
    currentVersionId: PropTypes.number,
    previewingVersionId: PropTypes.number,
    loadingPreviewVersionId: PropTypes.number,
    restoringVersionId: PropTypes.number,
    translate: PropTypes.func.isRequired,
};

export default withTranslate(VersionsShelf);
